import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NavBar } from '../components/NavBar';
import { Loader2, Utensils, Sparkles, Plus, X, Lock, Crown, RefreshCw, Calendar, Flame, Package, Clock, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export const MyNutritionPlansPage = () => {
  const { api, user } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);

  const isAdmin = !!user?.is_admin;
  const isPremium = !!user?.is_premium;
  const premiumLocked = !isPremium && !isAdmin;

  const regenerate = async (planId) => {
    if (!window.confirm('Regenerate this plan using your current macro targets? This replaces the current meals.')) return;
    setRegeneratingId(planId);
    try {
      const res = await api().post(`/nutrition-plans/regenerate-by-plan/${planId}`);
      setPlans((prev) => prev.map((p) => (p.id === planId ? res.data.plan : p)));
      toast.success('Plan regenerated to match your current macros');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Regenerate failed');
    }
    setRegeneratingId(null);
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const [p, list] = await Promise.all([
          api().get('/nutrition-plans/purchase/my-purchases'),
          api().get('/nutrition-plans/my/list').catch(() => ({ data: { plans: [] } })),
        ]);
        setPurchases(p.data.purchases || []);
        setPlans(list.data.plans || []);
      } catch (err) {
        console.debug('Failed to load:', err.message);
      }
      setLoading(false);
    };
    fetch();
  }, [api]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <NavBar />
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-12 w-12 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  const completedPurchases = purchases.filter((p) => p.status === 'completed' && p.nutrition_plan_id);
  const pendingPurchases = purchases.filter((p) => p.status === 'pending');

  return (
    <div className="min-h-screen bg-black" data-testid="my-nutrition-plans-page">
      <NavBar />

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2.5 py-1 mb-3">
              <Sparkles className="h-3 w-3" /> Your Library
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-wider leading-[1.05] bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent">
              My <span className="text-primary">Nutrition Plans</span>
            </h1>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">Your personalized AI-generated meal plans</p>
          </div>
          <Link to="/nutrition-plans">
            <button
              className="inline-flex items-center gap-2 text-sm font-semibold text-black bg-primary hover:bg-primary/90 rounded-full px-4 py-2.5 shadow-[0_4px_14px_rgba(250,204,21,0.25)] transition-all"
              data-testid="new-plan-btn"
            >
              <Plus className="h-4 w-4" /> Buy New Plan
            </button>
          </Link>
        </div>

        {premiumLocked && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 relative rounded-2xl border border-yellow-500/30 bg-gradient-to-br from-yellow-500/[0.12] via-yellow-500/[0.04] to-transparent p-5 overflow-hidden"
            data-testid="premium-required-banner"
          >
            <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-yellow-500/10 blur-3xl" />
            <div className="relative flex items-start gap-4">
              <div className="h-11 w-11 rounded-full bg-yellow-500/15 border border-yellow-500/30 flex items-center justify-center flex-shrink-0">
                <Lock className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading text-white uppercase tracking-wider text-sm mb-1">Premium members only</p>
                <p className="text-white/70 text-sm">
                  Nutrition plans are exclusive to premium subscribers. Unlock to view and manage your plans.
                </p>
              </div>
              <Link to="/subscription" className="flex-shrink-0">
                <button
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-black bg-yellow-400 hover:bg-yellow-300 uppercase tracking-wider rounded-full px-3.5 py-2 transition-all"
                  data-testid="unlock-premium-btn"
                >
                  <Crown className="h-3.5 w-3.5" /> Unlock
                </button>
              </Link>
            </div>
          </motion.div>
        )}

        {plans.length === 0 && completedPurchases.length === 0 && pendingPurchases.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative rounded-2xl border border-dashed border-white/15 bg-gradient-to-b from-zinc-900/60 to-black/40 p-10 sm:p-14 text-center max-w-2xl mx-auto overflow-hidden"
          >
            <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-56 w-56 rounded-full bg-primary/[0.08] blur-3xl" />
            <div className="relative">
              <div className="h-16 w-16 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center mx-auto mb-4">
                <Utensils className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-heading text-2xl text-white uppercase tracking-wider mb-2">No plans yet</h3>
              <p className="text-muted-foreground mb-6 max-w-sm mx-auto">Get your first AI-generated custom meal plan tailored to your goals.</p>
              <Link to="/nutrition-plans">
                <button className="inline-flex items-center gap-2 text-sm font-semibold text-black bg-primary hover:bg-primary/90 uppercase tracking-wider rounded-full px-5 py-3 shadow-[0_4px_14px_rgba(250,204,21,0.25)] transition-all">
                  <Sparkles className="h-4 w-4" /> Get My Plan
                </button>
              </Link>
            </div>
          </motion.div>
        ) : (
          <div className="space-y-10">
            {pendingPurchases.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <span className="h-1.5 w-1.5 rounded-full bg-yellow-400 shadow-[0_0_6px_currentColor]" />
                  <h2 className="font-heading text-xs sm:text-sm text-white uppercase tracking-[0.2em]">Pending</h2>
                  <span className="text-[10px] font-medium text-muted-foreground bg-white/[0.03] border border-white/10 rounded-full px-2 py-0.5">
                    {pendingPurchases.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {pendingPurchases.map((p) => (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/70 to-black/50 p-4 flex items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="h-10 w-10 rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center flex-shrink-0">
                          <Clock className="h-4 w-4 text-yellow-400" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium">{p.weeks}-week plan · ${p.amount}</p>
                          <p className="text-muted-foreground text-xs">Waiting for payment confirmation</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-yellow-300 bg-yellow-500/[0.08] border border-yellow-500/25 rounded-full px-2.5 py-1">
                          Pending
                        </span>
                        <button
                          disabled={cancellingId === p.id}
                          onClick={async () => {
                            if (!window.confirm('Cancel this pending purchase? This cannot be undone.')) return;
                            setCancellingId(p.id);
                            try {
                              await api().delete(`/nutrition-plans/purchase/${p.id}`);
                              setPurchases((prev) => prev.filter((x) => x.id !== p.id));
                              toast.success('Pending purchase cancelled');
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to cancel');
                            } finally {
                              setCancellingId(null);
                            }
                          }}
                          className="h-8 w-8 rounded-full border border-white/10 bg-white/[0.02] text-muted-foreground hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/[0.06] flex items-center justify-center transition-all"
                          data-testid={`cancel-pending-${p.id}`}
                          aria-label="Cancel pending purchase"
                        >
                          {cancellingId === p.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <X className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {(plans.length > 0 || completedPurchases.length > 0) && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_currentColor]" />
                  <h2 className="font-heading text-xs sm:text-sm text-white uppercase tracking-[0.2em]">Active Plans</h2>
                  <span className="text-[10px] font-medium text-muted-foreground bg-white/[0.03] border border-white/10 rounded-full px-2 py-0.5">
                    {plans.length}
                  </span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {plans.map((plan, idx) => {
                    const isStandalone = plan.source === 'standalone_purchase';
                    const sourceLabel = isStandalone ? 'Standalone' : 'Program';
                    const kcalStart = plan.overview?.daily_calories_start;
                    const kcalEnd = plan.overview?.daily_calories_end;
                    const kcalLabel = kcalStart
                      ? (kcalEnd && kcalEnd !== kcalStart ? `${kcalStart}–${kcalEnd}` : `${kcalStart}`)
                      : null;
                    return (
                      <motion.div
                        key={plan.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: idx * 0.04 }}
                        className="group relative rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-black/60 shadow-[0_4px_20px_rgba(0,0,0,0.25)] hover:border-primary/30 hover:shadow-[0_8px_30px_rgba(250,204,21,0.08)] transition-all overflow-hidden"
                        data-testid={`plan-${plan.id}`}
                      >
                        <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-primary/[0.05] blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />

                        <div className="relative p-5">
                          {/* Title row */}
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-start gap-2.5 min-w-0 flex-1">
                              <span className="h-2 w-2 mt-2 rounded-full bg-primary shadow-[0_0_8px_currentColor] flex-shrink-0" />
                              <h3 className="font-heading text-white uppercase tracking-wider text-base sm:text-lg leading-snug line-clamp-2">
                                {plan.program_title}
                              </h3>
                            </div>
                            {isStandalone && (
                              <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/[0.1] border border-primary/30 rounded-full px-2 py-1">
                                Purchased
                              </span>
                            )}
                          </div>

                          {/* Meta chips */}
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-white/80 bg-white/[0.04] border border-white/10 rounded-full px-2 py-0.5">
                              <Calendar className="h-3 w-3 text-primary" /> {plan.duration_weeks}w
                            </span>
                            {plan.overview?.meals_per_day && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-white/80 bg-white/[0.04] border border-white/10 rounded-full px-2 py-0.5">
                                <Utensils className="h-3 w-3 text-primary" /> {plan.overview.meals_per_day}/day
                              </span>
                            )}
                            {kcalLabel && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-white/80 bg-white/[0.04] border border-white/10 rounded-full px-2 py-0.5">
                                <Flame className="h-3 w-3 text-orange-400" /> {kcalLabel} kcal
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-white/60 bg-white/[0.02] border border-white/10 rounded-full px-2 py-0.5">
                              <Package className="h-3 w-3" /> {sourceLabel}
                            </span>
                          </div>

                          {/* Strategy quote — line clamped */}
                          {plan.overview?.strategy && (
                            <p className="text-white/70 text-sm italic leading-relaxed mb-4 line-clamp-3">
                              "{plan.overview.strategy}"
                            </p>
                          )}

                          {/* Actions */}
                          {plan.program_id ? (
                            <Link to={premiumLocked ? '/subscription' : `/program/${plan.program_id}`} className="block">
                              <button className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/25 rounded-full px-4 py-2.5 transition-all">
                                {premiumLocked ? (
                                  <><Lock className="h-3.5 w-3.5" /> Unlock With Premium</>
                                ) : (
                                  <>Open Program <ChevronRight className="h-3.5 w-3.5" /></>
                                )}
                              </button>
                            </Link>
                          ) : (
                            <div className="space-y-2">
                              <Link to={premiumLocked ? '/subscription' : `/nutrition-plans/view/${plan.id}`} className="block">
                                <button className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-black bg-primary hover:bg-primary/90 rounded-full px-4 py-2.5 shadow-[0_4px_14px_rgba(250,204,21,0.2)] transition-all">
                                  {premiumLocked ? (
                                    <><Lock className="h-3.5 w-3.5" /> Unlock With Premium</>
                                  ) : (
                                    <>View Plan <ChevronRight className="h-4 w-4" /></>
                                  )}
                                </button>
                              </Link>
                              {!premiumLocked && (
                                <button
                                  onClick={() => regenerate(plan.id)}
                                  disabled={regeneratingId === plan.id}
                                  className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium text-primary bg-primary/[0.06] hover:bg-primary/[0.12] border border-primary/25 hover:border-primary/50 rounded-full px-3 py-2 transition-all disabled:opacity-60"
                                  data-testid={`regenerate-plan-${plan.id}`}
                                >
                                  {regeneratingId === plan.id ? (
                                    <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Regenerating…</>
                                  ) : (
                                    <><RefreshCw className="h-3.5 w-3.5" /> Regenerate To Match My Macros</>
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
};
